import { useMemo, useState } from "react";
import { Row, Col, Table, Alert, Button, message } from "antd";
import { ColumnsType } from "antd/lib/table";
import { DownloadOutlined } from "@ant-design/icons";
import API, { AUTH_HEADER_NAME } from "common/apis";
import { generateHeaders } from "common/utils";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { getWhDivisionId } from "services/PersistService/PersistDivisionService";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";

const MESSAGE_KEY = "onboarding_details";

const DEFAULT_HEADERS: Record<string, string> = {
  userId: "User ID",
  name: "Name",
  mysaPartnerId: "MYSA Partner ID",
  currentStatus: "Current Status",
  alreadyCreated: "Already Created",
};

interface IOnboardingUploadRow {
  errors?: { message: string }[];
  [key: string]: any;
}

interface IOnboardingUploadResult {
  key?: string | null;
  total?: number;
  success?: number;
  skipped?: number;
  failed?: number;
  headerMapping?: string;
  successRows?: IOnboardingUploadRow[];
  failedRows?: IOnboardingUploadRow[];
  [key: string]: any;
}

const OnboardingDetailsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Mysa Account Details");
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    onboardingDownloadStatus,
    uploadOnboardingUrl,
    saveOnboardingUrl,
    downloadOnboardingThunk,
  } = config;
  const { authToken } = useAppSelector((state) => state.app.user);

  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadResult, setUploadResult] = useState<IOnboardingUploadResult>({});
  const [uploadKey, setUploadKey] = useState(0);

  const whDivId = getWhDivisionId();

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
    ...(whDivId && { "wh-div-id": whDivId.toString() }),
  };

  const handleOnboardingDownload = async () => {
    const result = await dispatch(downloadOnboardingThunk());
    if (downloadOnboardingThunk.rejected.match(result)) {
      const errMsg = (result.payload as { message?: string })?.message;
      message.error({ content: errMsg ?? "Download failed!", key: MESSAGE_KEY });
    }
  };

  const onUploading = () => {
    setHasErrorRows(false);
    setUploadResult({});
  };

  const onUploaded = (data: IOnboardingUploadResult) => {
    setUploadResult(data || {});
    setHasErrorRows(!!data?.failedRows?.length || !!data?.failed);
  };

  const successRows = uploadResult?.successRows || [];
  const failedRows = uploadResult?.failedRows || [];
  const successData = successRows.map((row, i) => ({ ...row, key: `data-success-${i}` }));
  const failedData = failedRows.map((row, i) => ({ ...row, key: `data-failed-${i}` }));
  const data: (IOnboardingUploadRow & { key: string })[] = [...failedData, ...successData];

  const tableHeaders = useMemo(() => {
    if (uploadResult?.headerMapping) {
      return generateHeaders(uploadResult.headerMapping + ",errors:Errors") || {};
    }
    const keysFromRows = new Set<string>();
    data.forEach((row) => {
      Object.keys(row).forEach((k) => {
        if (k !== "key" && k !== "errors") keysFromRows.add(k);
      });
    });
    const base: Record<string, string> = {};
    (keysFromRows.size ? Array.from(keysFromRows) : Object.keys(DEFAULT_HEADERS)).forEach((k) => {
      base[k] = DEFAULT_HEADERS[k] ?? k;
    });
    if (data.some((row) => row.errors?.length)) base.errors = "Errors";
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadResult]);

  const columns: ColumnsType<IOnboardingUploadRow & { key: string }> =
    Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: any, record: IOnboardingUploadRow & { key: string }) => {
        if (key === "errors") return <>{record.errors?.map((e) => e.message).join(", ")}</>;
        if (typeof text === "boolean") return <>{text ? "Yes" : "No"}</>;
        if (text === null || text === undefined) return <>-</>;
        return <>{text}</>;
      },
    }));

  const addBorderToErrorRows = (record: IOnboardingUploadRow & { key: string }) =>
    record.errors?.length ? "error-row" : "";

  const showTable = !!data.length;
  const canSave = showTable && !hasErrorRows && !!uploadResult?.key;

  const handleSave = async () => {
    if (!uploadResult?.key) return;
    setSaving(true);
    try {
      await API.post(saveOnboardingUrl, {}, { params: { key: uploadResult.key } });
      message.success({ content: "Onboarding details saved successfully.", key: MESSAGE_KEY });
      setUploadResult({});
      setHasErrorRows(false);
      setUploadKey((k) => k + 1);
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.message || "Save failed!";
      message.error({ content: errMsg, key: MESSAGE_KEY });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Mysa Account Details" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleOnboardingDownload}
            loading={onboardingDownloadStatus === "downloading"}
          >
            Download Mysa Account Details
          </Button>
        </AppActionStrip>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col style={{ width: "100%" }}>
          <AppUpload
            key={uploadKey}
            uploadUrl={uploadOnboardingUrl}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
          />
        </Col>
      </Row>

      {showTable && (
        <>
          {hasErrorRows && (
            <Row>
              <Col style={{ width: "100%" }}>
                <Alert
                  type="error"
                  message="Errors in sheet data."
                  description="Please correct errors and upload again."
                  showIcon
                  banner
                />
              </Col>
            </Row>
          )}
          <Row style={{ marginTop: "1rem" }}>
            <Col xs={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowClassName={addBorderToErrorRows}
                scroll={{ y: 500, x: "100vh" }}
                className="table-with-errors"
                pagination={false}
              />
            </Col>
          </Row>
          {canSave && (
            <Row style={{ marginTop: "1rem" }}>
              <Col>
                <Button type="primary" onClick={handleSave} loading={saving}>
                  Save
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

export default OnboardingDetailsPage;
